import { QIcon_check_filled, QIcon_check_outline, QIcon_outlink } from '@repo/icons/index'
import classNames from 'classnames'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { useEventTracking } from '../../../../helpers/gtmHelper'
import apis from '../../../../modules/apis'
import { axiosPost } from '../../../../modules/axiosHelper'
import { WEB_CAF_OVERVIEW_SIDE_MENU } from '../../../../modules/consts/caf'
import { APP_SCREEN_NAME } from '../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import useReturnFullUrl from '../../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_ai_analysis, n_review_lessons } from '../../../../modules/i18n/strings/auto/wording'
import { n_caf, n_system } from '../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import { locationMoveLocaleNewTab } from '../../../../modules/utils'
import { COLORS, STUDY_REVIEW } from '../../../../modules/vars'
import NewTag from '../../../common/menu/NewTag'
import JobAnalysisModal from '../common/JobAnalysis/JobAnalysisModal'
import useCafJobAnalysisSidePopup from './popup/useCafJobAnalysisSidePopup'
import useJobAnalysisPopup from './popup/useJobAnalysisPopup'

interface MenuProps {
  activeIndex: number
  setTabIndex: (index: number) => void
  todoChecked: boolean
  setTodoChecked: (checked: boolean) => void
  availableMenus?: number[]
}

const CAFOverviewSideMenu = ({ activeIndex, todoChecked, setTodoChecked, availableMenus = [1, 2, 3] }: MenuProps) => {
  const { t, isLocaleKO } = useTranslation()
  const { sendClickEvent } = useEventTracking()
  const router = useAppRouter()
  const lessonId = router.query.lessonId
  const { isShowAlertPopup, isShowJobInputPopup, info, jobStatus } = useJobAnalysisPopup()
  const [showAlertPopup, setShowAlertPopup] = useState(false)
  const [showJobInputPopup, setShowJobInputPopup] = useState(false)
  const { show, close } = useCafJobAnalysisSidePopup()
  const isWebview = useIsWebview()
  const getFullUrl = useReturnFullUrl()

  const handleClickButton = () => {
    sendClickEvent({
      button_name: 'ai_analysis_job_segment_side',
      object_title: jobStatus,
    })

    if (isShowJobInputPopup) {
      setShowJobInputPopup(true)
    } else if (isShowAlertPopup) {
      setShowAlertPopup(true)
    } else {
      if (isWebview) {
        router.push(urls.webview.caf.analysis.jobFilter, {
          appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
          appSendData: {
            url: getFullUrl(urls.webview.caf.analysis.jobFilter),
          },
        })
      } else {
        show()
      }
    }
  }
  const changeTab = (tab: number) => {
    router.push(urls.student.newPortal.lessons.reviewDetail.asCaf(lessonId, tab))
  }
  const onClickWhatIsCAF = () => {
    const cafLink = t([
      '/student/landing/blog/ai-analysis-about',
      '/student/landing/blog/ai-analysis-about-eng',
      '',
      '/student/landing/blog/english-related-17',
      '/student/landing/blog/english-related-13',
      '/student/landing/blog/ai-analysis-about-eng',
      '',
    ])
    locationMoveLocaleNewTab(cafLink)
  }

  const checkCAFTodo = async (id) => {
    const payload = {
      action_type: STUDY_REVIEW.CAF,
    }
    const response = await axiosPost(apis.lessons.review.study(id), payload)
    if (response?.success) {
      setTodoChecked(true)
      toast(t(n_caf.overview.check_caf_analysis.toast))
    } else {
      toast(t('Please try again.'))
    }
  }

  return (
    <div
      className={'hidden w-[160px] flex-col justify-between bg-white pt-[32px] sm:flex'}
      style={{ height: 'calc(100vh - 120px)' }}
    >
      <div className="fixed">
        {WEB_CAF_OVERVIEW_SIDE_MENU.filter((v) => availableMenus.includes(v.tag) || v.tag === 999).map((item, i) => (
          <div
            key={`${item.label}_${i}`}
            className={classNames(
              'mb-[4px] flex w-[100%] cursor-pointer items-center justify-start rounded-[70px] px-[12px] py-[8px]',
              {
                'bg-white': activeIndex != item.tab,
                'bg-purple-50': activeIndex == item.tab,
              }
            )}
            onClick={() => changeTab(item.tab)}
          >
            <div className="flex flex-row items-center justify-start">
              {item.icon(activeIndex === i ? COLORS.PURPLE500 : COLORS.GRAY500)}
              <span
                className={classNames('ml-[4px] font-bold', {
                  'text-gray-500': activeIndex != item.tab,
                  'text-purple-500': activeIndex == item.tab,
                })}
              >
                {t(item.label)}
              </span>
            </div>
          </div>
        ))}
        <div className="mt-[16px] w-[100%] border-t-[1px] border-gray-200"></div>
        <div
          className="mt-[20px] hidden w-[100%] cursor-pointer items-center justify-start whitespace-nowrap px-[16px] text-[14px] font-bold text-gray-700 sm:flex"
          onClick={handleClickButton}
        >
          {t(n_ai_analysis.side_menu_job_segment)}
          <NewTag className="ml-[4px]" />
        </div>
        <div
          className="mt-[20px] flex w-[100%] cursor-pointer items-center justify-start whitespace-nowrap px-[16px] text-[14px] font-bold text-gray-700"
          onClick={onClickWhatIsCAF}
        >
          {t(n_system.lesson_style_caf)}
          <QIcon_outlink color={COLORS.GRAY500} width={16} height={16} className="ml-[4px]" />
        </div>
      </div>
      <div className="fixed bottom-[36px] hidden items-center justify-center lg:flex">
        <div
          className="border-1 mx-auto flex w-[100%] cursor-pointer items-center justify-start whitespace-nowrap rounded-[7px] border-gray-300 px-[20px] py-[16px]"
          onClick={() => {
            checkCAFTodo(lessonId)
            sendClickEvent({ button_name: 'lesson_review_ai_complete' })
          }}
        >
          {todoChecked ? (
            <QIcon_check_filled className="mr-[8px] h-[20px] w-[20px]" color={COLORS.GREEN500} />
          ) : (
            <QIcon_check_outline className="mr-[8px] h-[20px] w-[20px]" color={COLORS.GRAY500} />
          )}
          <span className="font-medium text-gray-700">{t(n_review_lessons.review_completed_caf)}</span>
        </div>
      </div>
      <JobAnalysisModal
        isShowAlertPopup={isShowAlertPopup}
        showAlertPopup={showAlertPopup}
        setShowAlertPopup={setShowAlertPopup}
        info={info}
        isShowJobInputPopup={isShowJobInputPopup}
        showJobInputPopup={showJobInputPopup}
        setShowJobInputPopup={setShowJobInputPopup}
      />
    </div>
  )
}

export default CAFOverviewSideMenu
